import type { FieldOperation } from '@/components/Jobs/FieldClock'
import type { PrivateCaptureOperation } from '@/components/CrewPrivateCapture/CrewPrivateCapture'

/** Only explicit, reviewed field adapters belong here. Never queue arbitrary URLs. */
export interface CrewNoteOperation {
  id: string
  workspaceId: string
  userId: string
  jobId: string
  baseVersion: string
  deviceAt: string
  kind: 'note'
  text: string
}
export interface CaptureQueuedOperation {
  id: string; workspaceId: string; userId: string; jobId: string; deviceAt: string
  kind: 'private-capture'
  capture: PrivateCaptureOperation
}
export type CrewOperation = CrewNoteOperation | FieldOperation | CaptureQueuedOperation
export interface CrewReceipt { id: string; status: 'synced' | 'conflict'; recordedAt: string }
export interface OutboxRecord {
  scope: string
  operation: CrewOperation
  state: 'waiting' | 'quarantined' | 'synced' | 'conflict' | 'removed'
  receipt?: CrewReceipt
  error?: string
}
export interface OutboxStore {
  list(scope: string): Promise<OutboxRecord[]>
  get(scope: string, id: string): Promise<OutboxRecord | undefined>
  add(record: OutboxRecord): Promise<void>
  /** Atomically preserve an existing removed tombstone against older writes. */
  put(record: OutboxRecord): Promise<void>
}
export const scopeOf = (identity: { workspaceId: string; userId: string }) => `${encodeURIComponent(identity.workspaceId)}:${encodeURIComponent(identity.userId)}`
export type CrewTransport = (operation: CrewOperation) => Promise<CrewReceipt>

export class CrewOutbox {
  private syncing = false
  constructor(private store: OutboxStore, private activeScope: () => string | null) {}
  async enqueue(operation: CrewOperation) {
    const scope = scopeOf(operation)
    if (this.activeScope() !== scope) throw new Error('Prepare offline jobs for this account first')
    // add, never put: a reused ID cannot overwrite an operation awaiting ACK.
    await this.store.add({ scope, operation, state: 'waiting' })
  }
  async list(scope: string) {
    if (this.activeScope() !== scope) return []
    return this.store.list(scope)
  }
  async retry(scope: string, id: string) {
    if (this.activeScope() !== scope) throw new Error('Prepare offline jobs for this account first')
    const record = await this.store.get(scope, id)
    if (this.activeScope() !== scope) throw new Error('Prepare offline jobs for this account first')
    if (record?.state === 'quarantined') await this.store.put({ ...record, state: 'waiting', error: undefined })
  }
  /** Owner-requested device erasure or cleanup after confirmed server deletion. */
  async removeDeviceCapture(scope: string, id: string) {
    if (this.activeScope() !== scope) throw new Error('Return to the original account to finish device cleanup')
    const record = await this.store.get(scope, id)
    if (this.activeScope() !== scope) throw new Error('Return to the original account to finish device cleanup')
    if (!record || record.operation.kind !== 'private-capture') return
    await this.store.put({ ...record, state: 'removed', error: undefined, operation: { ...record.operation, capture: { ...record.operation.capture, base64: '', vendor: '', amountCents: null, note: '', fileName: 'Device copy removed' } } })
  }
  async sync(scope: string, send: CrewTransport) {
    if (this.syncing || this.activeScope() !== scope) return
    this.syncing = true
    try {
      for (const candidate of await this.store.list(scope)) {
        if (this.activeScope() !== scope) break
        if (candidate.state !== 'waiting') continue
        // A prior request may have been in flight while this record was erased
        // in another tab. Read its current state before dispatch, not the queue
        // snapshot captured at the start of this sync.
        const record = await this.store.get(scope, candidate.operation.id)
        if (this.activeScope() !== scope) break
        if (!record || record.scope !== scope || record.operation.id !== candidate.operation.id || record.state !== 'waiting') continue
        try {
          const receipt = await send(record.operation)
          if (receipt.id !== record.operation.id || !['synced', 'conflict'].includes(receipt.status) || !Number.isFinite(Date.parse(receipt.recordedAt))) throw new Error('No valid acknowledgment')
          if (this.activeScope() !== scope) break
          await this.store.put({ ...record, state: receipt.status, receipt, error: undefined })
        } catch (error) {
          if (this.activeScope() !== scope) break
          const status = (error as { response?: { status?: number } })?.response?.status
          const permanent = status != null && status >= 400 && status < 500 && ![401, 408, 425, 429].includes(status)
          await this.store.put({ ...record, state: permanent ? 'quarantined' : 'waiting', error: permanent ? 'Not accepted. Assignment or data needs attention; this record remains on this device.' : 'Not confirmed. Retry when connected and signed in.' })
          if (!permanent) break
        }
      }
    } finally { this.syncing = false }
  }
}
